import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Tabs,
  Tab,
  Divider,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  UploadFile as UploadFileIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  Preview as PreviewIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Avatar } from '@mui/material';

const signature = `
Best regards,
The GlobalCommand Center Team
`;

const emailTemplates = [
  `Hi {name},

Welcome to our service! We're glad to have you.

${signature}`,

  `Hello {name},

Your appointment is confirmed for {date}. Please let us know if you have questions.

${signature}`,

  `Dear {name},

Thank you for your purchase! Your order #{orderId} is being processed.

${signature}`,

  `Hi {name},

Reminder: Your subscription expires on {date}. Renew now to continue enjoying our service.

${signature}`,

  `Hello {name},

Here's an exclusive offer just for you!

${signature}`
];

export const EmailCampaign = () => {
  // Tab state
  const [tab, setTab] = useState(0);

  // Run Now state
  const [runNowFile, setRunNowFile] = useState(null);
  const [runNowSubject, setRunNowSubject] = useState('');
  const [runNowText, setRunNowText] = useState('');
  const [runNowTemplate, setRunNowTemplate] = useState('');

  const [runNowToFile, setRunNowToFile] = useState(null);
  const [runNowCcFile, setRunNowCcFile] = useState(null);
  const [runNowBccFile, setRunNowBccFile] = useState(null);

  // Run Later state
  const [runLaterFile, setRunLaterFile] = useState(null);
  const [runLaterSubject, setRunLaterSubject] = useState('');
  const [runLaterText, setRunLaterText] = useState('');
  const [runLaterTemplate, setRunLaterTemplate] = useState('');
  const [runLaterDateTime, setRunLaterDateTime] = useState(null);

  const [runLaterToFile, setRunLaterToFile] = useState(null);
  const [runLaterCcFile, setRunLaterCcFile] = useState(null);
  const [runLaterBccFile, setRunLaterBccFile] = useState(null);

  // Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState('');

  // Template modal state
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templatePreview, setTemplatePreview] = useState('');
  const [templateTarget, setTemplateTarget] = useState('runNow'); // or 'runLater'

  // Handlers for file upload
  const handleRunNowFileChange = (e) => {
    setRunNowFile(e.target.files[0]);
  };
  const handleRunLaterFileChange = (e) => {
    setRunLaterFile(e.target.files[0]);
  };

  // Handlers for template select
  const handleRunNowTemplateChange = (e) => {
    setRunNowTemplate(e.target.value);
    setRunNowText(e.target.value);
  };
  const handleRunLaterTemplateChange = (e) => {
    setRunLaterTemplate(e.target.value);
    setRunLaterText(e.target.value);
  };

  // Handlers for Run actions (stub)
  const handleRunNow = () => {
    // TODO: Implement API call to send email immediately
    alert('Run Now: Email sent!');
  };
  const handleRunLater = () => {
    // TODO: Implement API call to schedule email
    alert('Run Later: Email scheduled!');
  };

  // Handler for preview
  const handlePreview = (content) => {
    setPreviewContent(content);
    setPreviewOpen(true);
  };

  const openTemplateModal = (target) => {
    setTemplateTarget(target);
    setTemplateModalOpen(true);
  };
  const closeTemplateModal = () => setTemplateModalOpen(false);

  const selectTemplate = (tpl) => {
    setTemplatePreview(tpl);
    if (templateTarget === 'runNow') {
      setRunNowTemplate(tpl);
      setRunNowText(tpl);
    } else {
      setRunLaterTemplate(tpl);
      setRunLaterText(tpl);
    }
    setTemplateModalOpen(false);
  };

  const handleCsvChange = (setter) => (e) => {
    setter(e.target.files[0]);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ py: 5, bgcolor: '#f8fafc', minHeight: '100vh' }}>
        <Container maxWidth="md"> {/* Wider container for Gmail feel */}
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#4B5AB6', mb: 0.5 }}>
              Email Campaign
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748b', fontWeight: 400 }}>
              Send instant or scheduled emails with beautiful templates
            </Typography>
          </Box>

          <Paper
            elevation={8}
            sx={{
              borderRadius: 4,
              boxShadow: '0 8px 32px rgba(75,90,182,0.16)',
              p: 0,
              maxWidth: 700,
              mx: 'auto',
              overflow: 'hidden',
            }}
          >
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              sx={{
                background: '#f8fafc',
                color: '#4B5AB6',
                '& .Mui-selected': { color: '#4B5AB6 !important' },
                '& .MuiTabs-indicator': { backgroundColor: '#4B5AB6' }
              }}
            >
              <Tab
                label="Run Now"
                icon={<SendIcon />}
                iconPosition="start"
                sx={{ fontWeight: 700, fontSize: '1rem' }}
              />
              <Tab
                label="Run Later"
                icon={<ScheduleIcon />}
                iconPosition="start"
                sx={{ fontWeight: 700, fontSize: '1rem' }}
              />
            </Tabs>
            <Divider />
            <Box sx={{ p: 4, background: '#fff' }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: '#4B5AB6', width: 32, height: 32, fontSize: 18 }}>To</Avatar>
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<UploadFileIcon />}
                      sx={{
                        bgcolor: '#fff',
                        color: '#4B5AB6',
                        borderColor: '#4B5AB6',
                        fontWeight: 700,
                        borderRadius: 2,
                        '&:hover': { bgcolor: '#eef2ff' }
                      }}
                    >
                      Upload To CSV
                      <input type="file" accept=".csv" hidden onChange={handleCsvChange(tab === 0 ? setRunNowToFile : setRunLaterToFile)} />
                    </Button>
                    {(tab === 0 ? runNowToFile : runLaterToFile) && (
                      <Typography variant="body2" sx={{ color: '#64748b' }}>
                        {(tab === 0 ? runNowToFile : runLaterToFile).name}
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: '#6366f1', width: 32, height: 32, fontSize: 18 }}>CC</Avatar>
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<UploadFileIcon />}
                      sx={{
                        bgcolor: '#fff',
                        color: '#6366f1',
                        borderColor: '#6366f1',
                        fontWeight: 700,
                        borderRadius: 2,
                        '&:hover': { bgcolor: '#eef2ff' }
                      }}
                    >
                      Upload CC CSV
                      <input type="file" accept=".csv" hidden onChange={handleCsvChange(tab === 0 ? setRunNowCcFile : setRunLaterCcFile)} />
                    </Button>
                    {(tab === 0 ? runNowCcFile : runLaterCcFile) && (
                      <Typography variant="body2" sx={{ color: '#64748b' }}>
                        {(tab === 0 ? runNowCcFile : runLaterCcFile).name}
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: '#64748b', width: 32, height: 32, fontSize: 18 }}>BCC</Avatar>
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<UploadFileIcon />}
                      sx={{
                        bgcolor: '#fff',
                        color: '#64748b',
                        borderColor: '#64748b',
                        fontWeight: 700,
                        borderRadius: 2,
                        '&:hover': { bgcolor: '#eef2ff' }
                      }}
                    >
                      Upload BCC CSV
                      <input type="file" accept=".csv" hidden onChange={handleCsvChange(tab === 0 ? setRunNowBccFile : setRunLaterBccFile)} />
                    </Button>
                    {(tab === 0 ? runNowBccFile : runLaterBccFile) && (
                      <Typography variant="body2" sx={{ color: '#64748b' }}>
                        {(tab === 0 ? runNowBccFile : runLaterBccFile).name}
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Subject"
                    fullWidth
                    value={tab === 0 ? runNowSubject : runLaterSubject}
                    onChange={e => tab === 0 ? setRunNowSubject(e.target.value) : setRunLaterSubject(e.target.value)}
                    sx={{
                      mb: 2,
                      bgcolor: '#f8fafc',
                      borderRadius: 2,
                      '& .MuiOutlinedInput-root': { borderRadius: 2 }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    startIcon={<PreviewIcon />}
                    sx={{
                      mb: 2,
                      bgcolor: '#fff',
                      color: '#4B5AB6',
                      borderColor: '#4B5AB6',
                      fontWeight: 700,
                      borderRadius: 2,
                      '&:hover': { bgcolor: '#eef2ff' }
                    }}
                    onClick={() => openTemplateModal(tab === 0 ? 'runNow' : 'runLater')}
                  >
                    Choose Email Body Template
                  </Button>
                  <TextField
                    label="Email Body"
                    multiline
                    rows={14}
                    fullWidth
                    value={tab === 0 ? runNowText : runLaterText}
                    onChange={e => tab === 0 ? setRunNowText(e.target.value) : setRunLaterText(e.target.value)}
                    sx={{
                      bgcolor: '#f8fafc',
                      borderRadius: 2,
                      fontSize: '1.1rem',
                      '& .MuiOutlinedInput-root': { borderRadius: 2 }
                    }}
                  />
                </Grid>
                {tab === 1 && (
                  <Grid item xs={12}>
                    <DateTimePicker
                      label="Select Date & Time"
                      value={runLaterDateTime}
                      onChange={setRunLaterDateTime}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          sx={{
                            bgcolor: '#f8fafc',
                            borderRadius: 2,
                            '& .MuiOutlinedInput-root': { borderRadius: 2 }
                          }}
                        />
                      )}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
            <Divider />
            <Box sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              p: 3,
              background: '#f8fafc',
            }}>
              <Button
                variant="contained"
                startIcon={tab === 0 ? <SendIcon /> : <ScheduleIcon />}
                sx={{
                  background: 'linear-gradient(90deg, #4B5AB6 0%, #6366f1 100%)',
                  fontWeight: 700,
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  boxShadow: '0 2px 8px rgba(75,90,182,0.12)',
                  transition: 'transform 0.1s',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #6366f1 0%, #4B5AB6 100%)',
                    transform: 'scale(1.04)',
                  },
                }}
                onClick={tab === 0 ? handleRunNow : handleRunLater}
              >
                {tab === 0 ? 'Send' : 'Schedule'}
              </Button>
            </Box>
          </Paper>

          {/* Template Modal */}
          <Dialog
            open={templateModalOpen}
            onClose={closeTemplateModal}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(75,90,182,0.18)',
                background: '#fff',
              },
            }}
          >
            <DialogTitle sx={{ fontWeight: 700, color: '#4B5AB6', textAlign: 'center' }}>
              Choose an Email Body Template
            </DialogTitle>
            <DialogContent dividers>
              {emailTemplates.map((tpl, idx) => (
                <Paper
                  key={idx}
                  sx={{
                    p: 2,
                    mb: 2,
                    bgcolor: templatePreview === tpl ? '#e0e7ff' : '#f8fafc',
                    border: templatePreview === tpl ? '2px solid #4B5AB6' : '1px solid #e2e8f0',
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: '0.2s',
                    boxShadow: templatePreview === tpl ? '0 2px 8px #4B5AB633' : 'none',
                    '&:hover': { bgcolor: '#e0e7ff' },
                  }}
                  onClick={() => selectTemplate(tpl)}
                >
                  <Typography variant="body1" sx={{ color: '#475569', fontSize: '1.08rem' }}>
                    {tpl}
                  </Typography>
                </Paper>
              ))}
            </DialogContent>
            <DialogActions>
              <Button onClick={closeTemplateModal} sx={{ color: '#64748b' }}>Cancel</Button>
            </DialogActions>
          </Dialog>
        </Container>
      </Box>
    </LocalizationProvider>
  );
};

export default EmailCampaign;